/**
 * @reims-local/types — types partagés entre workers et apps.
 * Source: SPEC.md §7 (modèle de données D1).
 */

// ──────────────────────────────────────────────────────────────────────────
// Enums / constantes
// ──────────────────────────────────────────────────────────────────────────

export const SHOP_TYPES = ['SHOP_STANDARD', 'SHOP_FOOD', 'RESTAURANT', 'SERVICE'] as const;
export type ShopType = (typeof SHOP_TYPES)[number];

export const SHOP_STATUSES = [
  'pre_registered',
  'pending',
  'active',
  'suspended',
  'trial',
] as const;
export type ShopStatus = (typeof SHOP_STATUSES)[number];

export const SHOP_PLANS = ['starter', 'pro', 'premium'] as const;
export type ShopPlan = (typeof SHOP_PLANS)[number];

export const USER_ROLES = ['buyer', 'merchant', 'admin'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const ORDER_STATUSES = [
  'pending',
  'confirmed',
  'preparing',
  'ready',
  'picked_up',
  'cancelled',
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const RESERVATION_STATUSES = [
  'pending',
  'confirmed',
  'cancelled',
  'no_show',
] as const;
export type ReservationStatus = (typeof RESERVATION_STATUSES)[number];

export const RESERVATION_TYPES = ['table', 'service'] as const;
export type ReservationType = (typeof RESERVATION_TYPES)[number];

export const NOTIFICATION_CHANNELS = ['sms', 'email'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const NOTIFICATION_STATUSES = ['pending', 'sent', 'failed'] as const;
export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number];

export const SUPPORT_CATEGORIES = ['bug', 'suggestion', 'question', 'critical'] as const;
export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number];

export const SUPPORT_STATUSES = [
  'new',
  'in_progress',
  'waiting',
  'resolved',
  'closed',
] as const;
export type SupportStatus = (typeof SUPPORT_STATUSES)[number];

export const SUPPORT_SENDERS = ['merchant', 'admin'] as const;
export type SupportSender = (typeof SUPPORT_SENDERS)[number];

export const INVITATION_STATUSES = ['sent', 'opened', 'activated', 'expired'] as const;
export type InvitationStatus = (typeof INVITATION_STATUSES)[number];

export const PARTNER_TYPES = ['association', 'collectivite'] as const;
export type PartnerType = (typeof PARTNER_TYPES)[number];

export const CONTRACT_TIERS = ['essentiel', 'partenaire', 'premium'] as const;
export type ContractTier = (typeof CONTRACT_TIERS)[number];

/** 14 allergènes INCO (EU 1169/2011). */
export const ALLERGENS = [
  'gluten',
  'crustaces',
  'oeufs',
  'poissons',
  'arachides',
  'soja',
  'lait',
  'fruits_a_coque',
  'celeri',
  'moutarde',
  'sesame',
  'sulfites',
  'lupin',
  'mollusques',
] as const;
export type Allergen = (typeof ALLERGENS)[number];

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export interface OpeningHoursSlot {
  open: string; // "HH:MM"
  close: string; // "HH:MM"
}
export type OpeningHours = Partial<Record<Weekday, OpeningHoursSlot | null>>;

// ──────────────────────────────────────────────────────────────────────────
// Entités DB (section 7)
// ──────────────────────────────────────────────────────────────────────────

export interface Partner {
  id: string;
  name: string;
  type: PartnerType;
  contract_tier: ContractTier;
  included_shops: number;
  annual_fee_cents: number;
  start_date: string;
  renewal_date: string;
  status: string;
  created_at: string;
}

export interface Shop {
  id: string;
  partner_id: string | null;
  partner_included: boolean;
  name: string;
  slug: string;
  shop_type: ShopType;
  status: ShopStatus;
  plan: ShopPlan;
  stripe_account_id: string | null;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  address: string | null;
  city: string;
  phone: string | null;
  email: string | null;
  description: string | null;
  logo_url: string | null;
  cover_url: string | null;
  opening_hours: OpeningHours | null;
  min_order_delay_minutes: number;
  max_capacity: number | null;
  slot_duration_minutes: number;
  settings: Record<string, unknown>;
  stripe_onboarding_complete: boolean;
  charges_enabled: boolean;
  lat: number | null;
  lng: number | null;
  cgv_text: string | null;
  cgv_pdf_url: string | null;
  siret: string | null;
  vat_number: string | null;
  created_at: string;
  updated_at: string;
}

export interface User {
  id: string;
  email: string;
  phone: string | null;
  name: string | null;
  role: UserRole;
  shop_id: string | null;
  stripe_customer_id: string | null;
  no_show_count: number;
  reservations_blocked: boolean;
  cgu_accepted_at: string | null;
  cgu_version: string | null;
  password_hash: string | null;
  created_at: string;
}

/** Jamais exposé côté client — exclure password_hash. */
export type PublicUser = Omit<User, 'password_hash'>;

export interface Product {
  id: string;
  shop_id: string;
  name: string;
  description: string | null;
  price_cents: number;
  category: string | null;
  photo_url: string | null;
  stock: number;
  stock_alert_threshold: number;
  is_available: boolean;
  is_perishable: boolean;
  is_made_to_order: boolean;
  allergens: Allergen[];
  preparation_delay_minutes: number | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface Service {
  id: string;
  shop_id: string;
  name: string;
  description: string | null;
  duration_minutes: number;
  price_cents: number;
  is_available: boolean;
  created_at: string;
}

export interface Availability {
  id: string;
  shop_id: string;
  date: string;
  slot_time: string;
  max_capacity: number;
  booked_count: number;
  is_blocked: boolean;
  created_at: string;
}

export interface Order {
  id: string;
  order_number: string;
  shop_id: string;
  user_id: string;
  status: OrderStatus;
  pickup_slot: string;
  total_cents: number;
  stripe_payment_intent_id: string | null;
  stripe_transfer_id: string | null;
  gift_voucher_code: string | null;
  gift_voucher_amount: number;
  gift_voucher_validated: boolean;
  notes: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrderItemCustomization {
  removed?: string[];
  allergen_note?: string;
  extras?: string[];
}

export interface OrderItem {
  id: string;
  order_id: string;
  product_id: string;
  quantity: number;
  unit_price_cents: number;
  customization: OrderItemCustomization;
  created_at: string;
}

export interface Reservation {
  id: string;
  reservation_number: string;
  shop_id: string;
  user_id: string;
  service_id: string | null;
  reservation_type: ReservationType;
  status: ReservationStatus;
  date: string;
  time: string;
  covers: number | null;
  special_request: string | null;
  stripe_payment_intent_id: string | null;
  deposit_cents: number;
  reminder_sent_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  channel: NotificationChannel;
  status: NotificationStatus;
  provider_id: string | null;
  payload: Record<string, unknown>;
  sent_at: string | null;
  created_at: string;
}

export interface AdminMetric {
  id: string;
  metric_date: string;
  active_shops: number;
  new_shops: number;
  total_orders: number;
  total_reservations: number;
  gmv_cents: number;
  mrr_cents: number;
  created_at: string;
}

export interface ActivityLog {
  id: string;
  shop_id: string;
  event_type: string;
  entity_id: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface SupportTicket {
  id: string;
  ticket_number: string;
  shop_id: string;
  category: SupportCategory;
  subject: string;
  status: SupportStatus;
  priority: 1 | 2 | 3 | 4;
  sla_response_due_at: string | null;
  sla_resolve_due_at: string | null;
  first_response_at: string | null;
  resolved_at: string | null;
  admin_note: string | null;
  created_at: string;
  updated_at: string;
}

export interface SupportMessage {
  id: string;
  ticket_id: string;
  sender: SupportSender;
  content: string;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_size: number | null;
  is_read: boolean;
  created_at: string;
}

export interface ShopInvitation {
  id: string;
  shop_id: string;
  token: string; // SHA-256 hash stocké
  email: string;
  invited_by: string;
  status: InvitationStatus;
  expires_at: string;
  opened_at: string | null;
  activated_at: string | null;
  created_at: string;
}

// ──────────────────────────────────────────────────────────────────────────
// JWT / session
// ──────────────────────────────────────────────────────────────────────────

export interface JwtPayload {
  sub: string; // user id
  email: string;
  role: UserRole;
  shop_id?: string | null;
  iat: number;
  exp: number;
}

// ──────────────────────────────────────────────────────────────────────────
// KV — fichier d'analyse enseignes (section 33)
// ──────────────────────────────────────────────────────────────────────────

export interface AnalysisEnseigne {
  nom: string;
  adresse: string;
  telephone: string;
  email: string;
  type: ShopType;
  categorie: string;
  potentiel: 'FORT' | 'MOYEN' | 'FAIBLE';
  partenaire: string;
  notes: string;
  created?: boolean;
}

// ──────────────────────────────────────────────────────────────────────────
// API — enveloppes de réponse standard
// ──────────────────────────────────────────────────────────────────────────

export interface ApiError {
  error: string;
  code?: string;
  details?: Record<string, unknown>;
}

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: ApiError };

// ──────────────────────────────────────────────────────────────────────────
// Admin DTOs
// ──────────────────────────────────────────────────────────────────────────

export interface AdminMetricsResponse {
  active_shops: number;
  pending_shops: number;
  trial_shops: number;
  suspended_shops: number;
  invitations_sent: number;
  invitations_activated: number;
  mrr_cents: number;
  gmv_cents_30d: number;
  orders_30d: number;
}

export interface CreateShopInput {
  name: string;
  email: string;
  address?: string;
  phone?: string;
  shop_type: ShopType;
  partner_included?: boolean;
  partner_id?: string | null;
  description?: string;
  from_analysis?: boolean;
  admin_note?: string;
}

export interface ImportShopRow {
  nom: string;
  adresse: string;
  email: string;
  telephone: string;
  type: ShopType;
  categorie?: string;
  partenaire?: string;
  notes?: string;
}

export interface ImportShopResult {
  total: number;
  created: number;
  skipped: number;
  errors: Array<{ line: number; reason: string; data?: Partial<ImportShopRow> }>;
  created_shop_ids: string[];
}

export interface SendInvitationResult {
  invitation_id: string;
  expires_at: string;
  email_sent: boolean;
}
